import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SkuCardData } from "@/components/chat/SkuCard";

export interface ProductAddonOption extends SkuCardData {
  is_recommended: boolean;
  display_order: number;
}

const ADDON_SELECT = `
  display_order,
  is_recommended,
  addon_product_id,
  vendor_products!vendor_product_addons_addon_product_id_fkey (
    id,
    name,
    description,
    price,
    image_url,
    slug,
    vendor_category,
    is_voucher,
    lead_time_days,
    dietary_tags,
    vendor_partners ( vendor_name )
  )
`;

function rowToAddonOption(row: Record<string, unknown>): ProductAddonOption | null {
  const vp = row.vendor_products as Record<string, unknown> | null;
  if (!vp || typeof vp.id !== "string") return null;

  const partner = vp.vendor_partners as
    | { vendor_name?: string }
    | Array<{ vendor_name?: string }>
    | null;
  const vendorName = Array.isArray(partner)
    ? partner[0]?.vendor_name ?? "Lembra"
    : partner?.vendor_name ?? "Lembra";

  return {
    sku_id: vp.id,
    name: String(vp.name ?? "Add-on"),
    vendor_name: vendorName,
    vendor_category: (vp.vendor_category as string | null) ?? "gift",
    price_aud: Number(vp.price ?? 0),
    image_url: (vp.image_url as string | null) ?? null,
    slug: (vp.slug as string | null) ?? String(vp.id),
    description: (vp.description as string | null) ?? null,
    is_voucher: Boolean(vp.is_voucher),
    lead_time_days: (vp.lead_time_days as number | null) ?? null,
    dietary_tags: Array.isArray(vp.dietary_tags)
      ? (vp.dietary_tags as string[])
      : null,
    highlight_fields: [],
    is_recommended: Boolean(row.is_recommended),
    display_order: Number(row.display_order ?? 0),
  };
}

async function fetchProductAddons(productId: string): Promise<ProductAddonOption[]> {
  const { data, error } = await supabase
    .from("vendor_product_addons")
    .select(ADDON_SELECT)
    .eq("product_id", productId)
    .order("display_order", { ascending: true });

  if (error) throw new Error(error.message);

  return (data ?? [])
    .map((row) => rowToAddonOption(row as Record<string, unknown>))
    .filter((row): row is ProductAddonOption => row !== null);
}

/** Same-vendor upsells linked to a base vendor_products row. */
export function useProductAddons(productId: string | null, enabled = true) {
  return useQuery({
    queryKey: ["vendor-product-addons", productId],
    queryFn: () => {
      if (!productId) return [];
      return fetchProductAddons(productId);
    },
    enabled: enabled && Boolean(productId),
    staleTime: 5 * 60 * 1000,
  });
}
